/**
 * <cart-gift> — tiered free-gift engine (CLAIM model).
 * Liquid (cart-gift.liquid) computes basis/cat-ok/tiers and renders a rewards bar + a CLAIM card
 * for each unlocked gift not yet in the cart. The shopper clicks "Add" to claim it (offered, not
 * auto-added), or "Switch" to swap out whichever gift is already claimed for a different unlocked
 * one (works in either direction - claiming the mini then switching to the mystery disc, or back).
 * This also auto-REMOVES any gift line that is no longer qualified (dropped below tier or disc
 * removed). Gift lines carry property _gift; a matching Shopify automatic discount zeroes them.
 */
if (!customElements.get('cart-gift')) {
  class CartGift extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-claim-gift]').forEach(b => b.addEventListener('click', e => this.claim(e)));
      this.querySelectorAll('[data-switch-gift]').forEach(b => b.addEventListener('click', e => this.switchGift(e)));
      // Runs on every page load sitewide (this element lives in the persistent cart drawer) - defer
      // off the critical rendering path so it never competes with page-load-critical requests.
      const idle = window.requestIdleCallback || (fn => setTimeout(fn, 1000));
      idle(() => this.cleanup());
    }
    async claim(e) {
      const b = e.currentTarget, id = b.dataset.variant;
      if (!id || window.__cartGiftBusy) return;
      b.classList.add('is-loading'); b.disabled = true;
      window.__cartGiftBusy = true;
      try {
        const r = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: Number(id), quantity: 1, properties: { _gift: 'true' } }] }) });
        if (r.ok && window.dgdConfetti) window.dgdConfetti();
      } catch (err) { /* */ } finally { window.__cartGiftBusy = false; }
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
    async switchGift(e) {
      const b = e.currentTarget, id = b.dataset.variant;
      if (!id || window.__cartGiftBusy) return;
      b.classList.add('is-loading'); b.disabled = true;
      window.__cartGiftBusy = true;
      try {
        const cart = await fetch('/cart.js').then(r => r.json());
        const giftLines = (cart.items || []).filter(i => i.properties && i.properties._gift);
        for (const line of giftLines) {
          await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: line.key, quantity: 0 }) });
        }
        const r = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: Number(id), quantity: 1, properties: { _gift: 'true' } }] }) });
        if (r.ok && window.dgdConfetti) window.dgdConfetti();
      } catch (err) { /* */ } finally { window.__cartGiftBusy = false; }
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
    async cleanup() {
      if (window.__cartGiftBusy) return;
      let tiers;
      try { tiers = JSON.parse(this.dataset.tiers || '[]'); } catch (e) { return; }
      if (!tiers.length) return;
      window.__cartGiftBusy = true;
      const catOk = this.dataset.catOk === 'true';
      const basis = parseInt(this.dataset.basis || '0', 10);
      const allGiftV = new Set(tiers.map(t => t.v));
      const qualified = new Set(catOk ? tiers.filter(t => basis >= t.t).map(t => t.v) : []);
      let cart;
      try { cart = await fetch('/cart.js').then(r => r.json()); } catch (e) { window.__cartGiftBusy = false; return; }
      const giftLines = (cart.items || []).filter(i => (i.properties && i.properties._gift) || allGiftV.has(i.variant_id));
      const toRemove = giftLines.filter(i => !qualified.has(i.variant_id));
      if (!toRemove.length) { window.__cartGiftBusy = false; return; }
      try {
        for (const line of toRemove) {
          await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: line.key, quantity: 0 }) });
        }
      } catch (e) { /* */ } finally { window.__cartGiftBusy = false; }
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  }
  customElements.define('cart-gift', CartGift);
}
